/**
 * Company logos for lists that cannot join to `company_directory`.
 *
 * Dealflow and the raising feed get their logo through a PostgREST embed,
 * because those rows hang off `startup_profiles` and the relation is declared.
 * Two surfaces cannot do that:
 *
 *   - **Holdings** — `portfolio_companies.startup_id` is a bare uuid with NO
 *     foreign key, so there is no relation for PostgREST to traverse.
 *   - **Track record** — a claim about a past investment has no startup id at
 *     all; the company may not be on Inverio. Only the name is known.
 *
 * So this resolves them explicitly: one query per key space, cached as a single
 * unit, returning a lookup. It is a read of a public-ish column (directory rows
 * are readable by authenticated users, private ones only by their owner), so a
 * miss simply yields null and the caller falls back to initials.
 */

import { useQuery } from '@tanstack/react-query';

import { isSupabaseConfigured, supabase } from './client';

/** Lower-cased, trimmed — matches how the directory dedupes names. */
function nameKey(name: string): string {
  return name.trim().toLowerCase();
}

export interface CompanyLogoLookup {
  /** Logo for a company, by startup id when known and by name otherwise. */
  (name: string, startupId?: string | null): string | null;
}

/**
 * Resolve logos for a mixed list of companies.
 *
 * Pass every id and name the list renders; the hook de-duplicates. The query
 * key is derived from the sorted inputs, so it refetches only when the set
 * actually changes rather than on every render of a re-ordered list.
 */
export function useCompanyLogos(
  startupIds: (string | null | undefined)[],
  names: string[],
): CompanyLogoLookup {
  const ids = [...new Set(startupIds.filter((v): v is string => !!v))].sort();
  // Trimmed but NOT lower-cased: `.in('name', …)` matches the column as
  // stored, so lower-casing here would match nothing. `nameKey` is only for
  // the lookup map, where both sides are normalised.
  const uniqueNames = [
    ...new Map(names.map((n) => [nameKey(n), n.trim()])).values(),
  ]
    .filter(Boolean)
    .sort();

  const { data } = useQuery({
    queryKey: ['companyLogos', ids, uniqueNames] as const,
    queryFn: async () => {
      const byStartupId = new Map<string, string>();
      const byName = new Map<string, string>();
      if (!isSupabaseConfigured()) return { byStartupId, byName };

      // Two queries rather than one `.or()` string: an `.or()` filter is built
      // by interpolating values into a filter expression, so a company name
      // containing a comma or a parenthesis would corrupt it. `.in()` is
      // escaped by the client.
      if (ids.length > 0) {
        const { data: rows } = await supabase
          .from('company_directory')
          .select('startup_profile_id, name, logo_url')
          .in('startup_profile_id', ids)
          .not('logo_url', 'is', null);

        for (const row of rows ?? []) {
          if (row.startup_profile_id && row.logo_url) {
            byStartupId.set(row.startup_profile_id, row.logo_url);
          }
          if (row.name && row.logo_url) byName.set(nameKey(row.name), row.logo_url);
        }
      }

      if (uniqueNames.length > 0) {
        const { data: rows } = await supabase
          .from('company_directory')
          .select('startup_profile_id, name, logo_url')
          .in('name', uniqueNames)
          .not('logo_url', 'is', null);

        for (const row of rows ?? []) {
          if (row.name && row.logo_url) byName.set(nameKey(row.name), row.logo_url);
          if (row.startup_profile_id && row.logo_url) {
            byStartupId.set(row.startup_profile_id, row.logo_url);
          }
        }
      }

      return { byStartupId, byName };
    },
    enabled: isSupabaseConfigured() && (ids.length > 0 || uniqueNames.length > 0),
    staleTime: 5 * 60 * 1000,
  });

  return (name: string, startupId?: string | null) => {
    if (!data) return null;
    if (startupId) {
      const hit = data.byStartupId.get(startupId);
      if (hit) return hit;
    }
    return data.byName.get(nameKey(name)) ?? null;
  };
}
